import { Link } from '@tanstack/react-router';
import { ArrowRight, Check, ListChecks, Pencil, Phone, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  PHONE_SCREEN_TOPIC_MAX,
  nextRoundNumber,
  phoneScreenRoundName,
  placeInRound,
  roundRemovalRefusal,
  type JobDetail,
  type PhoneScreenPerson,
  type PhoneScreenPlace,
  type PhoneScreenRoundView,
  type PhoneScreenView,
} from '@nest-recruit/shared';
import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import type { Column } from '../../../components/data-table/types';
import { Input } from '../../../components/Input';
import { Popover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { PHONE_SCREEN_PLACE_TONES } from '../../../components/status-tones';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { toneCardClass } from '../../../components/Tone';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { EMPTY_SELECTION, isSelected, type SelectionState } from '../../../lib/selection';
import { registerCommands } from '../../../shell/command-registry';
import { CandidatePopup, type CandidatePanelTab } from '../../candidates/components/panel/CandidatePanel';
import { PHONE_SCREEN_SHOWS, validatePipelineSearch, type PhoneScreenShow, type PipelineSearch } from '../../pipeline/pipeline-search';
import { usePhoneScreen, usePhoneScreenResult, usePhoneScreenRounds, useSendToContact } from '../api/phone-screen-api';
import styles from './phone-screen.module.css';
import fit from '../../../components/FitPage.module.css';

// ADR 0079 — PHS-01, the optional phone Screening step on JOB-12, between ⑤ Shortlist and ⑥ Contact
// (workflow review D2; DECISIONS-LOG 2026-11-01 Q6, Q6b). HR adds "Screening 1" with what the call
// checks; everyone in the step is in it and gets Pass or Out in one tap (10-second Undo). "Screening 2"
// holds only Screening 1's passes. At any round HR ticks people and sends them to Contact. On a phone
// it is one card per person: the number dials, then Pass and Out — HR does this while calling.

interface StageProps {
  readonly detail: JobDetail;
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
}

const SHOW_LABELS: Readonly<Record<PhoneScreenShow, string>> = { waiting: 'Waiting', passed: 'Passed', out: 'Out' };

export function PhoneScreenStage({ detail, search, onSearch }: StageProps): ReactNode {
  const screening = usePhoneScreen(detail.id);
  if (screening.isPending) return <SkeletonLines count={6} />;
  if (screening.error?.status === 403) return <ForbiddenState resource="this screening list" contact="Your admin" isInset />;
  if (screening.error || !screening.data) return <ServerErrorState onRetry={() => void screening.refetch()} />;
  return <ScreeningBoard detail={detail} view={screening.data} search={search} onSearch={onSearch} />;
}

type Popped = { readonly kind: 'add'; readonly anchor: AnchorRect } | { readonly kind: 'edit'; readonly round: PhoneScreenRoundView; readonly anchor: AnchorRect };

function ScreeningBoard({ detail, view, search, onSearch }: StageProps & { readonly view: PhoneScreenView }): ReactNode {
  const jobId = detail.id;
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [popped, setPopped] = useState<Popped | null>(null);
  const [panelTab, setPanelTab] = useState<CandidatePanelTab>('cv');
  const { record, pending } = usePhoneScreenResult(jobId);
  const { send } = useSendToContact(jobId);
  const rounds = usePhoneScreenRounds(jobId);
  const update = (changes: Partial<PipelineSearch>): void => onSearch(validatePipelineSearch({ ...search, ...changes }));
  const roundCount = view.rounds.length;
  const roundNumber = Math.min(search.sround ?? 1, Math.max(roundCount, 1));
  const round = view.rounds.find((candidate) => candidate.number === roundNumber) ?? null;
  const show = search.sshow ?? 'waiting';
  const placeOf = (person: PhoneScreenPerson): PhoneScreenPlace | null => (round ? placeInRound(person, round.number) : person.isInStep ? 'waiting' : null);
  const rows = view.people.filter((person) => placeOf(person) === show);
  const selected = view.people.filter((person) => isSelected(selection, person.applicationId));
  const sendable = selected.filter((person) => person.isInStep);
  const canAdd = view.canEdit && view.isOn && nextRoundNumber(roundCount) !== null;
  const close = (): void => setPopped(null);
  const title = detail.publicTitle ?? detail.title;

  // 06-qol §1: each screening one palette search away while the stage is open.
  useEffect(() => {
    const go = (changes: Partial<PipelineSearch>): void => onSearch(validatePipelineSearch({ ...search, ...changes }));
    return registerCommands(
      view.rounds.map((candidate) => ({
        id: `phone-screen.round.${jobId}.${candidate.number}`,
        label: `Open ${phoneScreenRoundName(candidate.number)}: ${candidate.topic}`,
        section: 'action' as const,
        context: title,
        permission: 'application' as const,
        icon: ListChecks,
        keywords: ['screening', 'phone', 'call', 'screen'],
        run: () => go({ sround: candidate.number, sshow: undefined, app: undefined }),
      })),
    );
  }, [jobId, title, view.rounds, search, onSearch]);

  if (!view.isOn && view.people.length === 0) {
    return (
      <EmptyState
        title="Screening is off for this job"
        body="Switch it on from the Shortlist when you want to phone people before Contact. People the manager says yes to then wait here first."
        action={
          <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'shortlist' }} className={styles.action}>
            Open the shortlist
          </Link>
        }
        isInset
      />
    );
  }

  const resultOf = (person: PhoneScreenPerson): ReactNode => {
    const place = placeOf(person);
    if (place !== 'waiting' || !view.canEdit || !round) return <PlaceChip place={place} isPending={pending.has(person.applicationId)} />;
    const isBusy = pending.has(person.applicationId);
    return (
      <span className={styles.resultButtons}>
        <Button size="sm" variant="outline" icon={Check} disabled={isBusy} onClick={() => void record(person, round.number, 'pass')}>
          Pass
        </Button>
        <Button size="sm" variant="danger" icon={X} disabled={isBusy} onClick={() => void record(person, round.number, 'out')}>
          Out
        </Button>
      </span>
    );
  };

  const columns: readonly Column<PhoneScreenPerson>[] = [
    {
      id: 'name',
      header: 'Candidate',
      isIdentity: true,
      defaultWidth: 220,
      exportValue: (row) => row.candidate.fullName,
      cell: (row) => (
        <span className={styles.who}>
          <Avatar name={row.candidate.fullName} />
          <CandidateName person={row} onOpen={(app) => update({ app })} />
        </span>
      ),
    },
    { id: 'phone', header: 'Phone', defaultWidth: 170, exportValue: (row) => row.phone, cell: (row) => <PhoneLink phone={row.phone} /> },
    { id: 'score', header: 'Match', isNumeric: true, defaultWidth: 80, exportValue: (row) => row.score, cell: (row) => row.score ?? '—' },
    { id: 'result', header: round ? phoneScreenRoundName(round.number) : 'Result', defaultWidth: 200, exportValue: (row) => labelOf(placeOf(row)), cell: resultOf },
    { id: 'passed', header: 'Passed', isNumeric: true, defaultWidth: 100, exportValue: (row) => row.passedRound, cell: (row) => (roundCount === 0 ? '—' : `${row.passedRound} of ${roundCount}`) },
  ];

  const countOf = (key: PhoneScreenShow): number => view.people.filter((person) => placeOf(person) === key).length;
  // 06-qol §5: an empty state always carries the action that fills it.
  const emptyAction =
    show !== 'waiting' ? (
      <Button variant="outline" onClick={() => update({ sshow: undefined })}>
        Show who is waiting
      </Button>
    ) : round === null ? (
      <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'shortlist' }} className={styles.action}>
        Open the shortlist
      </Link>
    ) : round.number < roundCount ? (
      <Button variant="outline" onClick={() => update({ sround: round.number + 1, sshow: undefined })}>
        Open {phoneScreenRoundName(round.number + 1)}
      </Button>
    ) : (
      <Button variant="outline" onClick={() => update({ sshow: 'passed' })}>
        Show who passed
      </Button>
    );
  const emptyState = <EmptyState title={emptyTitleOf(show, round)} body={emptyBodyOf(show, round, roundCount)} action={emptyAction} isInset />;

  return (
    <div className={`${styles.stage} ${fit.fill}`}>
      <div className={styles.roundBar}>
        {roundCount > 0 && (
          <div className={styles.pills} role="group" aria-label="Screenings">
            {view.rounds.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                className={styles.pill}
                aria-pressed={candidate.number === roundNumber}
                onClick={() => update({ sround: candidate.number, sshow: undefined })}
              >
                {phoneScreenRoundName(candidate.number)}
                <span className={styles.pillCount}>{candidate.waiting}</span>
              </button>
            ))}
          </div>
        )}
        {canAdd && roundCount > 0 && (
          <Button variant="outline" icon={Plus} onClick={(event) => setPopped({ kind: 'add', anchor: anchorFrom(event.currentTarget) })}>
            Add {phoneScreenRoundName(roundCount + 1)}
          </Button>
        )}
        <span className={styles.waitingNote}>
          {view.waiting} {view.waiting === 1 ? 'person' : 'people'} in Screening
        </span>
      </div>

      {round ? (
        <RoundHead
          round={round}
          canEdit={view.canEdit}
          removeBlocked={roundRemovalRefusal(round.number, roundCount, view.people)}
          onEdit={(anchor) => setPopped({ kind: 'edit', round, anchor })}
          onRemove={() => {
            void rounds.remove(round.id, round.version).then((isRemoved) => {
              if (isRemoved) update({ sround: round.number - 1 > 1 ? round.number - 1 : undefined, sshow: undefined });
            });
          }}
        />
      ) : (
        <FirstRound canEdit={view.canEdit && view.isOn} waiting={view.waiting} onAdd={rounds.add} />
      )}

      {round && (
        <div className={styles.filters} role="group" aria-label="Show">
          {PHONE_SCREEN_SHOWS.map((key) => (
            <button key={key} type="button" className={styles.filter} aria-pressed={show === key} onClick={() => update({ sshow: key === 'waiting' ? undefined : key })}>
              {SHOW_LABELS[key]}
              <span className={styles.filterCount}>{countOf(key)}</span>
            </button>
          ))}
        </div>
      )}

      <div className={`${styles.table} ${fit.fill}`}>
        <DataTable
          screenId="phone-screen"
          label={round ? `${phoneScreenRoundName(round.number)}: ${round.topic}` : 'Screening'}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.applicationId}
          sort={[]}
          onSortChange={() => undefined}
          selection={selection}
          onSelectionChange={setSelection}
          isSelectable={view.canEdit}
          toneOf={(row) => {
            const place = placeOf(row);
            return place ? PHONE_SCREEN_PLACE_TONES[place] : undefined;
          }}
          emptyState={emptyState}
          bulkActions={
            <SendButton
              count={sendable.length}
              onSend={() => {
                setSelection(EMPTY_SELECTION);
                void send(sendable);
              }}
            />
          }
        />
      </div>

      {/* A phone gets one card per person: the number to tap, then Pass or Out (the call is happening now). */}
      <ul className={styles.cards} aria-label={round ? phoneScreenRoundName(round.number) : 'Screening'}>
        {rows.length === 0 && <li>{emptyState}</li>}
        {rows.map((person) => {
          const place = placeOf(person);
          return (
            <li key={person.applicationId} className={`${toneCardClass()} ${styles.card}`} data-tone={place ? PHONE_SCREEN_PLACE_TONES[place] : undefined}>
              <span className={styles.cardHead}>
                <span className={styles.who}>
                  <Avatar name={person.candidate.fullName} size="sm" />
                  <CandidateName person={person} onOpen={(app) => update({ app })} />
                </span>
                {person.score !== null && <span className={styles.meta}>Match {person.score}</span>}
              </span>
              {person.phone && (
                <a className={styles.cardPhone} href={`tel:${person.phone}`}>
                  <Phone size={16} aria-hidden />
                  {person.phone}
                </a>
              )}
              <span className={styles.cardActions}>
                {resultOf(person)}
                {view.canEdit && person.isInStep && (
                  <Button size="sm" variant="ghost" icon={ArrowRight} onClick={() => void send([person])}>
                    Send to Contact
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <Popover anchor={popped?.anchor ?? null} onClose={close} label={popped?.kind === 'edit' ? 'Change what it checks' : 'Add a screening'} align="left">
        {popped?.kind === 'add' && (
          <RoundForm
            label={`What does ${phoneScreenRoundName(roundCount + 1)} check?`}
            hint={`Only the people who passed ${phoneScreenRoundName(roundCount)} are in it.`}
            submitLabel="Add"
            onCancel={close}
            onSubmit={async (topic) => {
              close();
              const isAdded = await rounds.add(topic);
              if (isAdded) update({ sround: roundCount + 1, sshow: undefined });
            }}
          />
        )}
        {popped?.kind === 'edit' && (
          <RoundForm
            label={`What does ${phoneScreenRoundName(popped.round.number)} check?`}
            initial={popped.round.topic}
            submitLabel="Save"
            onCancel={close}
            onSubmit={async (topic) => {
              close();
              await rounds.rename(popped.round.id, popped.round.version, topic);
            }}
          />
        )}
      </Popover>

      <ScreeningPopup rows={rows} everyone={view.people} openApplicationId={search.app ?? null} tab={panelTab} onTabChange={setPanelTab} onOpen={(app) => update({ app: app ?? undefined })} />
    </div>
  );
}

function SendButton({ count, onSend }: { readonly count: number; readonly onSend: () => void }): ReactNode {
  return (
    <Button size="sm" variant="outline" icon={ArrowRight} disabled={count === 0} title={count === 0 ? 'Tick people who are still in Screening.' : undefined} onClick={onSend}>
      Send to Contact{count > 0 ? ` (${count})` : ''}
    </Button>
  );
}

/** What this round checks, in bold ink, with Change and (for the last, empty one) Take away. */
function RoundHead({
  round,
  canEdit,
  removeBlocked,
  onEdit,
  onRemove,
}: {
  readonly round: PhoneScreenRoundView;
  readonly canEdit: boolean;
  readonly removeBlocked: string | null;
  readonly onEdit: (anchor: AnchorRect) => void;
  readonly onRemove: () => void;
}): ReactNode {
  return (
    <section className={styles.roundHead} aria-label={phoneScreenRoundName(round.number)}>
      <div className={styles.roundText}>
        <h3 className={styles.roundTitle}>
          {phoneScreenRoundName(round.number)}: {round.topic}
        </h3>
        <p className={styles.meta}>
          {round.waiting} waiting · {round.passed} passed · {round.out} out{round.createdByName ? ` · added by ${round.createdByName}` : ''}
        </p>
      </div>
      {canEdit && (
        <span className={styles.roundActions}>
          <Button size="sm" variant="ghost" icon={Pencil} onClick={(event) => onEdit(anchorFrom(event.currentTarget))}>
            Change
          </Button>
          {removeBlocked === null && (
            <Button size="sm" variant="ghost" icon={Trash2} onClick={onRemove}>
              Take away
            </Button>
          )}
        </span>
      )}
    </section>
  );
}

/** Before any round: say what Screening 1 checks. Everyone in the step is in it. */
function FirstRound({ canEdit, waiting, onAdd }: { readonly canEdit: boolean; readonly waiting: number; readonly onAdd: (topic: string) => Promise<boolean> }): ReactNode {
  const people = `${waiting} ${waiting === 1 ? 'person' : 'people'}`;
  if (!canEdit) {
    return <p className={styles.firstNote}>HR hasn't added {phoneScreenRoundName(1)} yet. {people} are waiting for a call.</p>;
  }
  return (
    <section className={styles.firstRound} aria-label={`Add ${phoneScreenRoundName(1)}`}>
      <RoundForm
        label={`What does ${phoneScreenRoundName(1)} check?`}
        hint={`For example "Can they move for this job?". All ${people} in Screening are in it, and each gets Pass or Out.`}
        submitLabel={`Add ${phoneScreenRoundName(1)}`}
        onSubmit={async (topic) => {
          await onAdd(topic);
        }}
      />
    </section>
  );
}

function RoundForm({
  label,
  hint,
  initial = '',
  submitLabel,
  onSubmit,
  onCancel,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly initial?: string;
  readonly submitLabel: string;
  readonly onSubmit: (topic: string) => Promise<void>;
  readonly onCancel?: () => void;
}): ReactNode {
  const [topic, setTopic] = useState(initial);
  const trimmed = topic.trim();
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (trimmed === '') return;
    setTopic('');
    void onSubmit(trimmed);
  };
  return (
    <form className={styles.roundForm} onSubmit={submit}>
      <label className={styles.formLabel} htmlFor={`round-topic-${submitLabel}`}>
        {label}
      </label>
      {hint && <p className={styles.formHint}>{hint}</p>}
      <div className={styles.formRow}>
        <Input
          id={`round-topic-${submitLabel}`}
          value={topic}
          maxLength={PHONE_SCREEN_TOPIC_MAX}
          placeholder="Can they move for this job?"
          // A pop-up opened by the person's own press: their next key goes here.
          autoFocus={onCancel !== undefined}
          onChange={(event) => setTopic(event.target.value)}
        />
        <Button type="submit" variant="solid" disabled={trimmed === ''}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

/** The new result shows at once; while the server confirms, only a small mark beside it says so (§5a rule 4). */
function PlaceChip({ place, isPending }: { readonly place: PhoneScreenPlace | null; readonly isPending: boolean }): ReactNode {
  if (place === null) return '—';
  return (
    <span className={styles.placeChip} aria-busy={isPending || undefined}>
      <Chip tone={PHONE_SCREEN_PLACE_TONES[place]} plain={place !== 'waiting'}>
        {labelOf(place)}
      </Chip>
      {isPending && <span className={styles.pendingMark} aria-hidden />}
    </span>
  );
}

function labelOf(place: PhoneScreenPlace | null): string {
  return place === null ? '—' : SHOW_LABELS[place];
}

function PhoneLink({ phone }: { readonly phone: string | null }): ReactNode {
  if (!phone) return '—';
  return (
    <a className={styles.phone} href={`tel:${phone}`} onClick={(event) => event.stopPropagation()}>
      <Phone size={14} aria-hidden />
      {phone}
    </a>
  );
}

function CandidateName({ person, onOpen }: { readonly person: PhoneScreenPerson; readonly onOpen: (applicationId: string) => void }): ReactNode {
  return (
    <button
      type="button"
      className={styles.nameButton}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(person.applicationId);
      }}
    >
      {person.candidate.fullName}
    </button>
  );
}

/** The candidate pop-up, stepping through the people the list shows (as on Contact). */
function ScreeningPopup({
  rows,
  everyone,
  openApplicationId,
  tab,
  onTabChange,
  onOpen,
}: {
  readonly rows: readonly PhoneScreenPerson[];
  readonly everyone: readonly PhoneScreenPerson[];
  readonly openApplicationId: string | null;
  readonly tab: CandidatePanelTab;
  readonly onTabChange: (tab: CandidatePanelTab) => void;
  readonly onOpen: (applicationId: string | null) => void;
}): ReactNode {
  const index = rows.findIndex((row) => row.applicationId === openApplicationId);
  const person = rows[index] ?? everyone.find((candidate) => candidate.applicationId === openApplicationId);
  if (!person) return null;
  const previous = index > 0 ? rows[index - 1] : undefined;
  const next = index >= 0 ? rows[index + 1] : undefined;
  return (
    <CandidatePopup
      candidateId={person.candidate.id}
      tab={tab}
      onTabChange={onTabChange}
      onClose={() => onOpen(null)}
      {...(index >= 0
        ? {
            nav: {
              position: index + 1,
              total: rows.length,
              onPrevious: previous ? () => onOpen(previous.applicationId) : null,
              onNext: next ? () => onOpen(next.applicationId) : null,
            },
          }
        : {})}
    />
  );
}

function emptyTitleOf(show: PhoneScreenShow, round: PhoneScreenRoundView | null): string {
  const name = round ? phoneScreenRoundName(round.number) : 'Screening';
  if (show === 'passed') return `Nobody has passed ${name} yet`;
  if (show === 'out') return `Nobody was screened out in ${name}`;
  return `Nobody is waiting in ${name}`;
}

function emptyBodyOf(show: PhoneScreenShow, round: PhoneScreenRoundView | null, roundCount: number): string {
  if (show !== 'waiting') return 'Try Waiting, or another screening.';
  if (!round) return 'When the manager says yes on the shortlist, or HR goes ahead, the person waits here for a call.';
  if (round.number > 1) return `Only the people who passed ${phoneScreenRoundName(round.number - 1)} are in ${phoneScreenRoundName(round.number)}.`;
  return roundCount > 1 ? 'Everyone here has a result. Open the next screening, or send the passes to Contact.' : 'Everyone here has a result. Send the passes to Contact, or add another screening.';
}
